import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

export interface FamilyEvent {
  id: number;
  relativeId: number;
  relativeName: string;
  relativeGender?: string;
  eventType: string;
  eventDate: string;
  title?: string;
  description?: string;
  location?: string;
  relatedRelativeId?: number;
  relatedRelativeName?: string;
  createdAt: string;
  updatedAt?: string;
  level: number; // Level relative to root: 0 = root, negative = ancestors, positive = descendants
}

export interface FamilyTimelineResponse {
  rootRelativeId: number;
  rootRelativeName: string;
  events: FamilyEvent[];
  totalFamilyMembers: number;
}

@Component({
  selector: 'app-family-timeline',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './family-timeline.component.html',
  styleUrl: './family-timeline.component.css'
})
export class FamilyTimelineComponent implements OnInit, OnDestroy {
  relativeId: number | null = null;
  timeline: FamilyTimelineResponse | null = null;
  isLoading = true;
  errorMessage = '';
  selectedLevel: number = 1; // Default to 1 level
  
  // Progressive rendering
  displayedEvents: FamilyEvent[] = [];
  isRendering = false;
  loadingProgress = {
    total: 0,
    loaded: 0,
    percentage: 0,
    message: 'Loading timeline...',
    currentLevel: '',
    levelProgress: {
      current: 0,
      total: 0
    }
  };
  private renderingTimeouts: any[] = [];
  private eventsByLevel: Map<number, FamilyEvent[]> = new Map();

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private location: Location
  ) { }

  ngOnInit(): void {
    // Combine params and queryParams using combineLatest or nested subscribe
    this.route.params.subscribe(params => {
      this.relativeId = +params['id'];
      
      // Check for level query parameter
      this.route.queryParams.subscribe(queryParams => {
        if (queryParams['level']) {
          const level = +queryParams['level'];
          if (level === 1 || level === 2) {
            this.selectedLevel = level;
          }
        }
        
        if (this.relativeId) {
          this.loadFamilyTimeline(this.relativeId, this.selectedLevel);
        } else {
          this.errorMessage = 'Invalid relative ID';
          this.isLoading = false;
        }
      });
    });
  }

  onLevelChange(): void {
    if (this.relativeId) {
      this.loadFamilyTimeline(this.relativeId, this.selectedLevel);
    }
  }

  loadFamilyTimeline(id: number, level: number = 1): void {
    this.isLoading = true;
    this.isRendering = false;
    this.displayedEvents = [];
    this.errorMessage = '';
    
    // Reset progress and show immediately
    this.loadingProgress = {
      total: 0,
      loaded: 0,
      percentage: 0,
      message: 'Fetching family data from server...',
      currentLevel: '',
      levelProgress: {
        current: 0,
        total: 0
      }
    };
    
    // Clear any existing rendering timeouts
    this.clearAllTimeouts();
    
    // Show initial progress immediately
    this.showInitialProgress();
    
    this.http.get<{ success: boolean; data: FamilyTimelineResponse; message: string }>(
      `${environment.apiUrl}/api/relative/${id}/family-timeline?level=${level}`
    ).subscribe({
      next: (response) => {
        if (response.success) {
          this.timeline = response.data;
          this.loadingProgress.total = response.data.events.length;
          this.loadingProgress.message = 'Processing timeline data...';
          this.loadingProgress.percentage = 5; // Show some progress
          
          // Start progressive rendering immediately
          if (response.data.events.length > 0) {
            // Small delay to ensure UI updates
            setTimeout(() => {
              this.progressivelyRenderEvents(response.data.events);
            }, 100);
          } else {
            this.isLoading = false;
            this.isRendering = false;
            this.loadingProgress.message = 'No events found';
            this.loadingProgress.percentage = 100;
          }
        } else {
          this.errorMessage = response.message || 'Failed to load family timeline';
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('Error loading family timeline:', error);
        this.errorMessage = error.error?.message || 'Failed to load family timeline. Please try again.';
        this.isLoading = false;
        this.isRendering = false;
      }
    });
  }

  private showInitialProgress(): void {
    // Simulate progress while waiting for API response
    let simulatedProgress = 0;
    const progressInterval = setInterval(() => {
      if (!this.isLoading || this.loadingProgress.total > 0) {
        clearInterval(progressInterval);
        return;
      }
      
      simulatedProgress = Math.min(simulatedProgress + 2, 15); // Cap at 15% while waiting
      this.loadingProgress.percentage = simulatedProgress;
      
      // Update message periodically
      if (simulatedProgress < 5) {
        this.loadingProgress.message = 'Connecting to server...';
      } else if (simulatedProgress < 10) {
        this.loadingProgress.message = 'Fetching family data...';
      } else {
        this.loadingProgress.message = 'Loading family timeline...';
      }
    }, 200);
    
    // Store interval to clear if needed
    this.renderingTimeouts.push(progressInterval as any);
  }

  progressivelyRenderEvents(events: FamilyEvent[]): void {
    this.isRendering = true;
    this.displayedEvents = [];
    this.loadingProgress.loaded = 0;
    // Start from 10% (data received) instead of 0%
    this.loadingProgress.percentage = 10;
    this.loadingProgress.message = 'Preparing timeline...';
    this.eventsByLevel.clear();
    
    // Group events by level
    events.forEach(event => {
      const level = event.level;
      if (!this.eventsByLevel.has(level)) {
        this.eventsByLevel.set(level, []);
      }
      this.eventsByLevel.get(level)!.push(event);
    });
    
    // Sort events within each level by date (most recent first)
    this.eventsByLevel.forEach((levelEvents, level) => {
      levelEvents.sort((a, b) => 
        new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime()
      );
    });
    
    // Get levels in order: negative (ancestors) first, then 0 (root), then positive (descendants)
    const levels = Array.from(this.eventsByLevel.keys()).sort((a, b) => {
      if (a < 0 && b < 0) return a - b; // Ancestors: -2, -1
      if (a === 0) return -1; // Root comes after ancestors
      if (b === 0) return 1;
      return a - b; // Descendants: 1, 2
    });
    
    this.loadingProgress.levelProgress.total = levels.length;
    this.loadingProgress.total = events.length;
    
    // Render level by level
    this.renderLevelsProgressively(levels, 0);
  }

  private renderLevelsProgressively(levels: number[], levelIndex: number): void {
    if (levelIndex >= levels.length) {
      // All levels rendered
      setTimeout(() => {
        this.isLoading = false;
        this.isRendering = false;
        this.loadingProgress.message = `Timeline loaded! (${this.loadingProgress.total} events)`;
        setTimeout(() => {
          if (this.loadingProgress.loaded === this.loadingProgress.total) {
            this.loadingProgress.message = '';
          }
        }, 2000);
      }, 300);
      return;
    }
    
    const level = levels[levelIndex];
    const levelEvents = this.eventsByLevel.get(level) || [];
    const levelLabel = this.getLevelDescription(level);
    
    this.loadingProgress.currentLevel = levelLabel;
    this.loadingProgress.levelProgress.current = levelIndex + 1;
    this.loadingProgress.message = `Loading ${levelLabel}... (${levelIndex + 1}/${levels.length} levels)`;
    
    // Render events for this level
    this.renderEventsForLevel(levelEvents, level, () => {
      // Move to next level after a short delay
      setTimeout(() => {
        this.renderLevelsProgressively(levels, levelIndex + 1);
      }, 200);
    });
  }

  private renderEventsForLevel(events: FamilyEvent[], level: number, onComplete: () => void): void {
    if (events.length === 0) {
      onComplete();
      return;
    }
    
    // Calculate delay based on number of events in this level
    const baseDelay = events.length > 20 ? 30 : events.length > 10 ? 40 : 50;
    let renderedInLevel = 0;
    const levelLabel = this.getLevelDescription(level);
    
    events.forEach((event, index) => {
      const timeout = setTimeout(() => {
        this.displayedEvents.push(event);
        renderedInLevel++;
        this.loadingProgress.loaded++;
        // Calculate percentage: 10% for data received, 90% for rendering (10% to 100%)
        const renderingProgress = (this.loadingProgress.loaded / this.loadingProgress.total) * 90;
        this.loadingProgress.percentage = Math.round(10 + renderingProgress);
        
        // Update message with level-specific info
        this.loadingProgress.message = `${levelLabel}: ${renderedInLevel}/${events.length} events (${this.loadingProgress.loaded}/${this.loadingProgress.total} total)`;
        
        // Check if all events in this level are rendered
        if (renderedInLevel === events.length) {
          onComplete();
        }
      }, index * baseDelay);
      
      this.renderingTimeouts.push(timeout);
    });
  }

  private clearAllTimeouts(): void {
    this.renderingTimeouts.forEach(timeout => {
      if (typeof timeout === 'number') {
        clearTimeout(timeout);
      } else {
        clearInterval(timeout);
      }
    });
    this.renderingTimeouts = [];
  }

  ngOnDestroy(): void {
    this.clearAllTimeouts();
  }

  getEventTypeClass(eventType: string): string {
    switch (eventType) {
      case 'Birth': return 'event-birth';
      case 'Marriage': return 'event-marriage';
      case 'Child': return 'event-child';
      case 'JobStart': return 'event-job-start';
      case 'JobEnd': return 'event-job-end';
      case 'Education': return 'event-education';
      case 'Death': return 'event-death';
      default: return 'event-custom';
    }
  }

  getEventTypeIcon(eventType: string): string {
    switch (eventType) {
      case 'Birth': return '👶';
      case 'Marriage': return '💍';
      case 'Child': return '🍼';
      case 'JobStart': return '💼';
      case 'JobEnd': return '🔚';
      case 'Education': return '🎓';
      case 'Death': return '⚰️';
      default: return '✨';
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  getRelativeColor(relativeName: string): string {
    // Generate a consistent color based on the name
    let hash = 0;
    for (let i = 0; i < relativeName.length; i++) {
      hash = relativeName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 70%, 50%)`;
  }

  getEventColor(eventType: string): string {
    const colors: { [key: string]: string } = {
      'Birth': '#4CAF50',
      'Marriage': '#E91E63',
      'Child': '#2196F3',
      'JobStart': '#FF9800',
      'JobEnd': '#9E9E9E',
      'Education': '#9C27B0',
      'Death': '#424242',
      'Other': '#607D8B'
    };
    return colors[eventType] || colors['Other'];
  }

  getLevelLabel(level: number): string {
    if (level === 0) {
      return 'Root';
    } else if (level < 0) {
      return `Level ${Math.abs(level)} Up`;
    } else {
      return `Level ${level} Down`;
    }
  }

  getLevelDescription(level: number): string {
    if (level === 0) {
      return 'Root Person';
    } else if (level === -1) {
      return 'Parent';
    } else if (level === -2) {
      return 'Grandparent';
    } else if (level === 1) {
      return 'Child';
    } else if (level === 2) {
      return 'Grandchild';
    } else if (level < 0) {
      return `${Math.abs(level)} Generations Up`;
    } else {
      return `${level} Generations Down`;
    }
  }

  goBack(): void {
    this.location.back();
  }

  getRemainingEventsCount(): number[] {
    const remaining = this.loadingProgress.total - this.displayedEvents.length;
    return Array(Math.min(remaining, 10)).fill(0); // Show max 10 skeleton loaders
  }
}

